import { UserCard } from "@/components/cards";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import type { ClassNameProps } from "@msuite/ui/types";
import { cn } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import type { Tables } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import React from "react";

/** Props Interface */
interface WikiPageBylineProps extends ClassNameProps {
	page: Pick<Tables<"wikiPages">, "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;
}

/**
 * Who touched the page last and when, beneath the title. A page nobody has edited since it was
 * written shows only its author and creation date; once edited, the editor leads and the creation
 * moves to the end of the line. Times are relative, with the exact moment in the tooltip.
 */
export function WikiPageByline({ page, className }: WikiPageBylineProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const wasEdited = !!page.updatedAt;
	const lead = wasEdited ? page.updatedBy : page.createdBy;
	const createdOn = page.createdAt ? moment(page.createdAt).dateFormat() : null;

	/** Render */
	return (
		<div className={cn("h-stack flex-wrap text-sm", className)}>
			{lead && (
				// Name only. The byline is one quiet line under a title, and a portrait in it pulls
				// the eye before the text does; the card that opens on hover still carries it.
				<UserCard
					userId={lead}
					variant="plain"
					fontSize="sm"
					withAvatar={false}
				/>
			)}
			{wasEdited && page.updatedAt && (
				<Tooltip label={moment(page.updatedAt).shortFormat()}>
					<span className="opacity-60">
						{t("wiki.last_edited", { time: moment(page.updatedAt).fromNow() })}
					</span>
				</Tooltip>
			)}
			{createdOn && !wasEdited && (
				<span className="opacity-60">{t("wiki.created_on", { date: createdOn })}</span>
			)}
			{createdOn && wasEdited && (
				<React.Fragment>
					<span className="opacity-40">·</span>
					{page.createdBy ? (
						<UserCard
							userId={page.createdBy}
							render={(author) => (
								<span className="opacity-60">
									{t("wiki.created_on_by", { date: createdOn, name: getFullName(author) })}
								</span>
							)}
						/>
					) : (
						<span className="opacity-60">{t("wiki.created_on", { date: createdOn })}</span>
					)}
				</React.Fragment>
			)}
		</div>
	);
}
